import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css, } from '@emotion/react';
import { Fragment, useCallback, useEffect, useRef, useState, } from 'react';
import colors from '../../styles/colors';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { useSwipe, } from '../../utils/useSwipe';
import { useTransitionOnRender } from '../../utils/useTransitionOnRender';
import { PANEL_ON_CLOSE_EVENT_NAME, PANEL_ON_OPEN_EVENT_NAME, PANEL_ON_RESIZE_EVENT_NAME, } from './Panel.constants';
export const HEADER_HEIGHT = 44;
export const SWIPE_EXCESS_HEIGHT = 32;
const DISPLAYED_STYLES = {
    closed: (delta, peek = 0) => ({
        transform: `translate3d(-50%, calc(100% - ${SWIPE_EXCESS_HEIGHT + peek - delta.y}px), 0)`,
    }),
    offscreen: () => undefined,
    open: (delta) => ({
        transform: `translate3d(
      -50%,
      ${Math.max(-SWIPE_EXCESS_HEIGHT, delta.y)}px,
      0
    )`,
    }),
    partial: (delta, peek = 0) => ({
        transform: `translate3d(
    -50%,
    calc(100% - ${HEADER_HEIGHT + SWIPE_EXCESS_HEIGHT + peek - delta.y}px),
    0
  )`,
    }),
};
const getOuterStyles = ({ isSwiping, }) => [
    css `
    background: ${colors.gray100};
    border-top-left-radius: ${theme.spacing.x3};
    border-top-right-radius: ${theme.spacing.x3};
    bottom: -${SWIPE_EXCESS_HEIGHT}px;
    box-shadow: 0px 2px 11px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    left: 50%;
    max-height: 100%;
    padding-bottom: ${SWIPE_EXCESS_HEIGHT}px;
    position: absolute;
    transform: translate3d(-50%, 100%, 0);
    transition: transform 0.3s ease;
    width: 100%;

    @media (pointer: fine) {
      &:active {
        transition: none;
      }
    }

    ${theme.media.greaterThan('large')(css `
      max-width: 25rem;
    `)}
  `,
    isSwiping &&
        css `
      transition: none;
    `,
];
const headerButtonStyles = css `
  align-items: center;
  background: none;
  border: 0;
  cursor: pointer;
  display: flex;
  flex: 1 0 auto;
  height: ${HEADER_HEIGHT}px;
  justify-content: center;
  width: 100%;

  &:after {
    background: ${colors.gray500};
    border-radius: ${theme.spacing.base};
    content: '';
    height: ${theme.spacing.base};
    width: ${theme.spacing.x8};
  }
`;
const scrimBaseStyles = css `
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: rgba(0, 0, 0, 0);
  pointer-events: none;
  transition: background 0.3s ease;
`;
const scrimOpenStyles = css `
  background: rgba(0, 0, 0, 0.3);
  pointer-events: all;
`;
export const Panel = ({ alwaysVisibleContent, children, closedDisplayState = 'offscreen', id, initialDisplayState = 'offscreen', initialPeek = 200, onAfterClose, onPanelOpen, shouldShowScrim, }) => {
    const [displayState, setDisplayState,] = useState(initialDisplayState);
    const [isToggleEnabled, setIsToggleEnabled,] = useState(true);
    const container = useRef(null);
    const alwaysVisibleContainer = useRef(null);
    const getOpenHeight = () => container.current ? container.current.getBoundingClientRect().height - SWIPE_EXCESS_HEIGHT : 0;
    // When the content inside of the panel changes sizes,
    // fire a new event denoting that new size
    const resizeObserver = useRef(new ResizeObserver(() => {
        window.requestAnimationFrame(() => {
            const ev = new CustomEvent(PANEL_ON_RESIZE_EVENT_NAME, {
                detail: {
                    height: getOpenHeight(),
                },
            });
            window.dispatchEvent(ev);
        });
    }));
    const panelContent = useRef(null);
    useEffect(() => {
        // Need to copy this here to use it in cleanup
        const resizeObserverInstance = resizeObserver.current;
        if (panelContent.current != null) {
            resizeObserver.current.observe(panelContent.current);
        }
        return () => {
            resizeObserverInstance.disconnect();
        };
    }, 
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [
        panelContent.current,
    ]);
    const { handleTransitionEnd: handleRenderTransitionEnd, shouldAnimate, shouldRender, } = useTransitionOnRender({
        container,
        isDisplayed: displayState !== 'offscreen',
    });
    const handleTransitionEnd = (event) => {
        // Re-enable toggle
        setIsToggleEnabled(true);
        // Run transition hook end
        handleRenderTransitionEnd(event);
        // Run onClose callback if appropriate
        if (displayState === 'closed' || displayState === 'offscreen') {
            if (onAfterClose) {
                onAfterClose();
            }
        }
    };
    const handleOpen = () => {
        setDisplayState('open');
        setIsToggleEnabled(false);
        if (onPanelOpen) {
            onPanelOpen();
        }
        const ev = new CustomEvent(PANEL_ON_OPEN_EVENT_NAME, {
            detail: {
                height: getOpenHeight(),
                id,
            },
        });
        window.dispatchEvent(ev);
    };
    const getClosedHeight = () => {
        var _a;
        if (closedDisplayState !== 'closed') {
            return 0;
        }
        return (((_a = alwaysVisibleContainer.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect().height) || 0) + HEADER_HEIGHT;
    };
    const handleClose = () => {
        setDisplayState(closedDisplayState);
        setIsToggleEnabled(false);
        const ev = new CustomEvent(PANEL_ON_CLOSE_EVENT_NAME, {
            detail: {
                height: getClosedHeight(),
                id,
            },
        });
        window.dispatchEvent(ev);
    };
    const handleToggle = () => {
        // Prevent toggling immediately after performing a swipe change
        if (isToggleEnabled) {
            displayState === 'open' ? handleClose() : handleOpen();
        }
    };
    const handleSwipeEnd = useCallback((swipeDirection) => {
        if (swipeDirection === 'up') {
            if (displayState === 'closed' || displayState === 'partial') {
                handleOpen();
            }
            return;
        }
        if (swipeDirection === 'down') {
            if (displayState === 'open' || displayState === 'partial') {
                handleClose();
            }
        }
    }, 
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [
        displayState,
    ]);
    const { dragDelta, handleScrollableChild, handleStartSwipe, isSwiping, } = useSwipe({
        allowedDirections: [
            'up',
            'down',
        ],
        onSwipeEnd: handleSwipeEnd,
    });
    // Fire an initial onOpen event
    useEffect(() => {
        if (shouldRender && container.current) {
            const ev = new CustomEvent(PANEL_ON_OPEN_EVENT_NAME, {
                detail: {
                    height: container.current.getBoundingClientRect().height,
                    id,
                },
            });
            window.dispatchEvent(ev);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [
        shouldRender,
        container.current,
    ]);
    if (!shouldRender) {
        return _jsx(Fragment, {});
    }
    const peekDistance = displayState === 'partial' ? initialPeek : getClosedHeight();
    const outerStyles = getOuterStyles({
        displayState,
        dragDelta,
        isSwiping,
        peekDistance,
        shouldAnimate,
    });
    return (_jsxs(Fragment, { children: [shouldShowScrim && (_jsx("div", { css: [
                    scrimBaseStyles,
                    displayState === 'open' && scrimOpenStyles,
                ], onClick: handleClose })), _jsxs("div", Object.assign({ css: outerStyles, onMouseDown: handleStartSwipe, onTouchStart: handleStartSwipe, onTransitionEnd: handleTransitionEnd, ref: container, style: shouldAnimate
                    ? DISPLAYED_STYLES[displayState](dragDelta, peekDistance)
                    : undefined }, { children: [_jsx("button", Object.assign({ css: headerButtonStyles, onClick: handleToggle, type: "button" }, { children: _jsxs("span", Object.assign({ css: styleUtils.visuallyHidden }, { children: [displayState === 'open' ? 'Close' : 'Open', ' ', "panel"] })) })), _jsxs("div", Object.assign({ css: displayState === 'open' &&
                            css `
              overflow-y: auto;
              overscroll-behavior: none;
            `, onScroll: handleScrollableChild, ref: panelContent }, { children: [alwaysVisibleContent && (_jsx("div", Object.assign({ ref: alwaysVisibleContainer }, { children: alwaysVisibleContent }))), children] }))] }))] }));
};
